import React from 'react';

interface CommitteeEmblemProps {
  id: string;
  className?: string;
}

const IMAGE_EMBLEMS: Record<string, string> = {
  'unga': '/committees/unga.png',
  'ecosoc': '/committees/ecosoc.png',
  'lok-sabha': '/committees/lok-sabha.png',
  'aippm': '/committees/aippm.png',
  'dead-poets-society': '/committees/dead-poets-society.png',
  'ip': '/committees/ip.png',
  'fifa': '/committees/fifa.png',
  'ipl': '/committees/ipl.png',
};

export const CommitteeEmblem: React.FC<CommitteeEmblemProps> = ({ id, className = "w-full h-full" }) => {
  if (IMAGE_EMBLEMS[id]) {
    return (
      <img
        src={IMAGE_EMBLEMS[id]}
        alt={`${id} emblem`}
        className={`w-full h-full object-contain ${className}`}
        loading="lazy"
        draggable={false}
      />
    );
  }

  switch (id) {
    case 'unga':
      // United Nations Official Emblem: Polar projection world grid embraced by olive laurel branches
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Outer Laurel Wreath Left */}
          <path d="M 68 165 C 40 145 28 105 38 65 C 44 45 58 30 75 22" strokeWidth="2.5" strokeLinecap="round" />
          {/* Left Laurel Leaves */}
          <path d="M 72 160 C 58 152 50 140 52 128 C 58 135 68 142 72 160 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 58 138 C 42 126 38 110 42 98 C 48 106 56 114 58 138 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 46 108 C 34 94 34 78 40 68 C 46 76 52 86 46 108 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 44 78 C 36 64 40 50 48 42 C 52 50 54 62 44 78 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 54 50 C 50 38 58 26 68 22 C 70 30 68 42 54 50 Z" fill="currentColor" fillOpacity="0.4" />

          {/* Outer Laurel Wreath Right */}
          <path d="M 132 165 C 160 145 172 105 162 65 C 156 45 142 30 125 22" strokeWidth="2.5" strokeLinecap="round" />
          {/* Right Laurel Leaves */}
          <path d="M 128 160 C 142 152 150 140 148 128 C 142 135 132 142 128 160 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 142 138 C 158 126 162 110 158 98 C 152 106 144 114 142 138 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 154 108 C 166 94 166 78 160 68 C 154 76 148 86 154 108 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 156 78 C 164 64 160 50 152 42 C 148 50 146 62 156 78 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 146 50 C 150 38 142 26 132 22 C 130 30 132 42 146 50 Z" fill="currentColor" fillOpacity="0.4" />

          {/* Bottom Laurel Tie Ribbon */}
          <path d="M 90 172 C 100 166 100 166 110 172" strokeWidth="2.5" />
          <circle cx="100" cy="168" r="4" fill="currentColor" />

          {/* Concentric Polar Circles */}
          <circle cx="100" cy="98" r="50" strokeWidth="2" strokeDasharray="4 3" />
          <circle cx="100" cy="98" r="38" strokeWidth="1.5" />
          <circle cx="100" cy="98" r="24" strokeWidth="1.5" />
          <circle cx="100" cy="98" r="10" strokeWidth="1.5" />
          <circle cx="100" cy="98" r="2.5" fill="currentColor" />

          {/* Radial Azimuthal Longitude Grid Lines */}
          <line x1="100" y1="46" x2="100" y2="150" strokeWidth="1.5" />
          <line x1="48" y1="98" x2="152" y2="98" strokeWidth="1.5" />
          <line x1="63" y1="61" x2="137" y2="135" strokeWidth="1" strokeDasharray="3 3" />
          <line x1="137" y1="61" x2="63" y2="135" strokeWidth="1" strokeDasharray="3 3" />

          {/* North Star Zenith */}
          <path d="M 100 10 L 102 18 L 110 20 L 102 22 L 100 30 L 98 22 L 90 20 L 98 18 Z" fill="currentColor" />
        </svg>
      );

    case 'ecosoc':
      // United Nations ECOSOC: Global economic balance beam & social progress pillar within UN branches
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* UN Laurel Wreath */}
          <path d="M 66 166 C 36 146 26 106 38 66 C 46 42 62 26 78 20" strokeWidth="2" strokeLinecap="round" />
          <path d="M 134 166 C 164 146 174 106 162 66 C 154 42 138 26 122 20" strokeWidth="2" strokeLinecap="round" />
          {/* Leaves */}
          <path d="M 52 130 C 40 120 42 104 48 94 C 54 104 60 114 52 130 Z" fill="currentColor" fillOpacity="0.3" />
          <path d="M 148 130 C 160 120 158 104 152 94 C 146 104 140 114 148 130 Z" fill="currentColor" fillOpacity="0.3" />

          {/* Central World Globe Axis */}
          <ellipse cx="100" cy="115" rx="42" ry="24" strokeWidth="1.5" strokeDasharray="3 2" />
          <ellipse cx="100" cy="115" rx="42" ry="42" strokeWidth="2" />
          <line x1="100" y1="73" x2="100" y2="157" strokeWidth="1.5" strokeDasharray="3 2" />

          {/* Scales of Economic Balance */}
          {/* Fulcrum pillar */}
          <line x1="100" y1="45" x2="100" y2="115" strokeWidth="3" strokeLinecap="round" />
          <polygon points="94,48 106,48 100,38" fill="currentColor" />
          {/* Balance crossbeam */}
          <line x1="56" y1="52" x2="144" y2="52" strokeWidth="3" strokeLinecap="round" />
          {/* Left Pan */}
          <line x1="56" y1="52" x2="44" y2="82" strokeWidth="1.5" />
          <line x1="56" y1="52" x2="68" y2="82" strokeWidth="1.5" />
          <path d="M 40 82 Q 56 94 72 82 Z" fill="currentColor" fillOpacity="0.4" strokeWidth="1.5" />
          {/* Right Pan */}
          <line x1="144" y1="52" x2="132" y2="82" strokeWidth="1.5" />
          <line x1="144" y1="52" x2="156" y2="82" strokeWidth="1.5" />
          <path d="M 128 82 Q 144 94 160 82 Z" fill="currentColor" fillOpacity="0.4" strokeWidth="1.5" />

          {/* Bar Chart Growth Glyph at base */}
          <rect x="86" y="132" width="7" height="16" fill="currentColor" fillOpacity="0.5" />
          <rect x="96" y="126" width="7" height="22" fill="currentColor" fillOpacity="0.5" />
          <rect x="106" y="120" width="7" height="28" fill="currentColor" fillOpacity="0.5" />
        </svg>
      );

    case 'lok-sabha':
      // Lok Sabha: Colonnaded Sansad Bhavan structure with central 24-spoke Ashoka Chakra
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Outer Parliament Perimeter Ring */}
          <circle cx="100" cy="100" r="78" strokeWidth="2.5" />
          <circle cx="100" cy="100" r="70" strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx="100" cy="100" r="54" strokeWidth="1.5" />

          {/* 36 Fluted Columns around Parliament colonnade */}
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i * 15 * Math.PI) / 180;
            const x1 = 100 + 56 * Math.cos(angle);
            const y1 = 100 + 56 * Math.sin(angle);
            const x2 = 100 + 68 * Math.cos(angle);
            const y2 = 100 + 68 * Math.sin(angle);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="2.5" strokeLinecap="round" />;
          })}

          {/* Central Ashoka Chakra Wheel */}
          <circle cx="100" cy="100" r="32" strokeWidth="2.5" />
          <circle cx="100" cy="100" r="8" fill="currentColor" />
          {/* 24 spokes */}
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i * 15 * Math.PI) / 180;
            const x1 = 100 + 8 * Math.cos(angle);
            const y1 = 100 + 8 * Math.sin(angle);
            const x2 = 100 + 32 * Math.cos(angle);
            const y2 = 100 + 32 * Math.sin(angle);
            return <line key={`spoke-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="1.5" />;
          })}

          {/* Top Indian Apex Flame Emblem */}
          <path d="M 92 14 C 96 6 104 6 108 14 C 104 18 96 18 92 14 Z" fill="currentColor" />
          <line x1="90" y1="20" x2="110" y2="20" strokeWidth="2" />
        </svg>
      );

    case 'aippm':
      // AIPPM: Parliamentary Chamber Gavel & Democracy Wheel
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Outer Radiating Council Rays */}
          <circle cx="100" cy="100" r="80" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.6" />
          <circle cx="100" cy="100" r="68" strokeWidth="2" />

          {/* Democratic Ashoka Chakra core */}
          <circle cx="100" cy="100" r="28" strokeWidth="2" />
          <circle cx="100" cy="100" r="6" fill="currentColor" />
          {Array.from({ length: 16 }).map((_, i) => {
            const angle = (i * 22.5 * Math.PI) / 180;
            const x1 = 100 + 6 * Math.cos(angle);
            const y1 = 100 + 6 * Math.sin(angle);
            const x2 = 100 + 28 * Math.cos(angle);
            const y2 = 100 + 28 * Math.sin(angle);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="1.2" />;
          })}

          {/* Prominent Presiding Officer Gavel crossing diagonal */}
          {/* Gavel Head */}
          <rect x="122" y="32" width="28" height="46" rx="4" transform="rotate(45 136 55)" strokeWidth="2.5" fill="currentColor" fillOpacity="0.3" />
          {/* Gavel Handle */}
          <rect x="70" y="80" width="12" height="90" rx="3" transform="rotate(45 76 125)" strokeWidth="2.5" fill="currentColor" fillOpacity="0.4" />
          {/* Soundblock base */}
          <path d="M 40 148 L 76 126 L 86 142 L 50 164 Z" strokeWidth="2" fill="currentColor" fillOpacity="0.3" />

          {/* Parliamentary Microphones left & right */}
          <path d="M 48 94 C 44 80 50 68 62 68 C 66 80 58 92 48 94 Z" fill="currentColor" fillOpacity="0.4" />
          <line x1="56" y1="92" x2="70" y2="108" strokeWidth="2" />
        </svg>
      );

    case 'rajputana-yuddh-parishad':
      // Rajputana Yuddh Parishad: Crossed Rajput Talwar Scimitars, Mewar Dhal Shield & Surya Crest
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Mewar Surya (Sun God) Radiant Crest */}
          <circle cx="100" cy="100" r="74" strokeWidth="1.5" strokeDasharray="3 3" />
          {Array.from({ length: 16 }).map((_, i) => {
            const angle = (i * 22.5 * Math.PI) / 180;
            const x1 = 100 + 58 * Math.cos(angle);
            const y1 = 100 + 58 * Math.sin(angle);
            const x2 = 100 + 72 * Math.cos(angle);
            const y2 = 100 + 72 * Math.sin(angle);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="2.5" strokeLinecap="round" />;
          })}

          {/* Crossed Rajput Talwars (Curved Scimitar Swords) */}
          {/* Sword 1 (Top-Left to Bottom-Right) */}
          <path d="M 32 36 Q 78 72 166 164" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M 32 36 Q 64 54 110 88" strokeWidth="2" />
          {/* Hilt & Disc Pommel 1 */}
          <circle cx="28" cy="32" r="7" strokeWidth="2.5" fill="currentColor" fillOpacity="0.5" />
          <line x1="38" y1="26" x2="24" y2="44" strokeWidth="3" />

          {/* Sword 2 (Top-Right to Bottom-Left) */}
          <path d="M 168 36 Q 122 72 34 164" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M 168 36 Q 136 54 90 88" strokeWidth="2" />
          {/* Hilt & Disc Pommel 2 */}
          <circle cx="172" cy="32" r="7" strokeWidth="2.5" fill="currentColor" fillOpacity="0.5" />
          <line x1="162" y1="26" x2="176" y2="44" strokeWidth="3" />

          {/* Central Rajput Dhal (War Shield) */}
          <circle cx="100" cy="100" r="46" strokeWidth="3.5" fill="#141414" fillOpacity="0.8" />
          <circle cx="100" cy="100" r="42" strokeWidth="1.5" strokeDasharray="4 2" />
          <circle cx="100" cy="100" r="30" strokeWidth="2" />
          {/* Central Sun Face / Boss */}
          <circle cx="100" cy="100" r="10" strokeWidth="2" fill="currentColor" fillOpacity="0.4" />
          {/* 4 Traditional Brass Bosses/Studs */}
          <circle cx="85" cy="85" r="4.5" fill="currentColor" />
          <circle cx="115" cy="85" r="4.5" fill="currentColor" />
          <circle cx="85" cy="115" r="4.5" fill="currentColor" />
          <circle cx="115" cy="115" r="4.5" fill="currentColor" />
        </svg>
      );

    case 'dead-poets-society':
      // Dead Poets Society (Welton Academy): Crossed Quill Pens, Open Poetry Tome & Laurels
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Outer Classical Wreath */}
          <path d="M 52 152 C 32 124 32 76 54 48 C 66 32 80 24 100 24" strokeWidth="2" strokeLinecap="round" />
          <path d="M 148 152 C 168 124 168 76 146 48 C 134 32 120 24 100 24" strokeWidth="2" strokeLinecap="round" />

          {/* Crossed Classical Feathered Quill Pens */}
          {/* Quill 1 */}
          <path d="M 44 42 Q 78 82 156 160" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 44 42 C 54 48 64 62 60 76 C 54 74 48 66 44 42 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 40 38 L 46 44" strokeWidth="2" />

          {/* Quill 2 */}
          <path d="M 156 42 Q 122 82 44 160" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 156 42 C 146 48 136 62 140 76 C 146 74 152 66 156 42 Z" fill="currentColor" fillOpacity="0.4" />
          <path d="M 160 38 L 154 44" strokeWidth="2" />

          {/* Open Vintage Poetry Tome / Book */}
          <path d="M 100 102 C 84 94 62 94 46 98 L 46 148 C 62 144 84 144 100 152 C 116 144 138 144 154 148 L 154 98 C 138 94 116 94 100 102 Z" strokeWidth="2.5" fill="#121212" fillOpacity="0.8" />
          <line x1="100" y1="102" x2="100" y2="152" strokeWidth="2.5" />
          {/* Poetic verse lines */}
          <line x1="56" y1="112" x2="88" y2="110" strokeWidth="1.5" opacity="0.6" />
          <line x1="56" y1="122" x2="90" y2="120" strokeWidth="1.5" opacity="0.6" />
          <line x1="56" y1="132" x2="82" y2="130" strokeWidth="1.5" opacity="0.6" />
          <line x1="112" y1="110" x2="144" y2="112" strokeWidth="1.5" opacity="0.6" />
          <line x1="110" y1="120" x2="144" y2="122" strokeWidth="1.5" opacity="0.6" />
          <line x1="118" y1="130" x2="144" y2="132" strokeWidth="1.5" opacity="0.6" />

          {/* Eternal Candle Flame of Expression */}
          <path d="M 100 68 C 96 76 96 82 100 88 C 104 82 104 76 100 68 Z" fill="currentColor" />
          <line x1="100" y1="88" x2="100" y2="98" strokeWidth="2" />
        </svg>
      );

    case 'ip':
      // International Press: Rangefinder Press Camera, Aperture Iris & Reporter Fountain Pen
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Framing Aperture / Focus Brackets */}
          <path d="M 34 54 L 34 34 L 54 34" strokeWidth="2.5" />
          <path d="M 166 54 L 166 34 L 146 34" strokeWidth="2.5" />
          <path d="M 34 146 L 34 166 L 54 166" strokeWidth="2.5" />
          <path d="M 166 146 L 166 166 L 146 166" strokeWidth="2.5" />

          {/* Press Camera Body */}
          <rect x="42" y="68" width="116" height="78" rx="6" strokeWidth="3" fill="#141414" fillOpacity="0.8" />
          {/* Camera Top Rangefinder Prism */}
          <path d="M 72 68 L 80 50 L 120 50 L 128 68 Z" strokeWidth="2.5" fill="currentColor" fillOpacity="0.3" />
          {/* Shutter Button & Flash Dial */}
          <rect x="52" y="60" width="12" height="8" rx="2" strokeWidth="2" fill="currentColor" />
          <circle cx="138" cy="58" r="5" strokeWidth="2" />

          {/* Large Camera Lens Rings */}
          <circle cx="100" cy="107" r="30" strokeWidth="3" />
          <circle cx="100" cy="107" r="22" strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx="100" cy="107" r="14" strokeWidth="2" />
          <circle cx="100" cy="107" r="5" fill="currentColor" />
          {/* Lens reflection glint */}
          <path d="M 92 97 Q 100 93 108 97" strokeWidth="1.5" strokeLinecap="round" />

          {/* Reporter Press Microphone / Pen Badge Banner */}
          <rect x="74" y="152" width="52" height="18" rx="2" strokeWidth="2" fill="currentColor" fillOpacity="0.3" />
          <text x="100" y="165" textAnchor="middle" fill="currentColor" fontSize="10" fontFamily="monospace" fontWeight="bold" stroke="none">
            PRESS
          </text>
        </svg>
      );

    case 'fifa':
      // FIFA Mega Auction: Official Trophy Silhouette & Football (Soccer) Heraldic Sphere
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Outer Global Laurel Victory Wreath */}
          <path d="M 50 162 C 26 138 24 92 42 56 C 54 36 72 24 90 20" strokeWidth="2" strokeLinecap="round" />
          <path d="M 150 162 C 174 138 176 92 158 56 C 146 36 128 24 110 20" strokeWidth="2" strokeLinecap="round" />

          {/* Central FIFA World Cup Trophy Silhouette */}
          {/* Globe at pinnacle */}
          <circle cx="100" cy="58" r="24" strokeWidth="2.5" fill="#141414" fillOpacity="0.8" />
          {/* Football panel lines on trophy ball */}
          <polygon points="100,48 108,54 105,63 95,63 92,54" strokeWidth="1.5" fill="currentColor" fillOpacity="0.4" />
          <line x1="100" y1="48" x2="100" y2="34" strokeWidth="1.5" />
          <line x1="108" y1="54" x2="120" y2="48" strokeWidth="1.5" />
          <line x1="105" y1="63" x2="114" y2="76" strokeWidth="1.5" />
          <line x1="95" y1="63" x2="86" y2="76" strokeWidth="1.5" />
          <line x1="92" y1="54" x2="80" y2="48" strokeWidth="1.5" />

          {/* Two ascending spiraling athlete figures holding the globe */}
          <path d="M 80 82 Q 92 90 92 120 L 88 150" strokeWidth="3" strokeLinecap="round" />
          <path d="M 120 82 Q 108 90 108 120 L 112 150" strokeWidth="3" strokeLinecap="round" />
          <path d="M 84 84 Q 100 104 100 136" strokeWidth="2.5" />

          {/* Stepped Malachite Trophy Base */}
          <rect x="80" y="150" width="40" height="10" rx="1" strokeWidth="2.5" fill="currentColor" fillOpacity="0.4" />
          <rect x="74" y="160" width="52" height="12" rx="2" strokeWidth="2.5" fill="currentColor" fillOpacity="0.6" />
          <line x1="78" y1="166" x2="122" y2="166" strokeWidth="1.5" stroke="currentColor" />

          {/* Star Accents */}
          <path d="M 100 14 L 102 18 L 107 19 L 103 22 L 104 27 L 100 24 L 96 27 L 97 22 L 93 19 L 98 18 Z" fill="currentColor" />
        </svg>
      );

    case 'ipl':
      // IPL Player Auction: Iconic IPL Batsman Silhouette, Wicket Stumps & Seamed Cricket Ball
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" className={className}>
          {/* Dynamic Motion Arc / Orbit */}
          <path d="M 28 144 C 36 68 96 32 172 64" strokeWidth="2.5" strokeDasharray="5 3" />
          <path d="M 38 156 C 78 184 144 178 178 132" strokeWidth="1.5" strokeDasharray="3 3" />

          {/* Cricket Wicket Stumps & Bails (Left Side) */}
          <line x1="50" y1="96" x2="50" y2="168" strokeWidth="3" strokeLinecap="round" />
          <line x1="58" y1="96" x2="58" y2="168" strokeWidth="3" strokeLinecap="round" />
          <line x1="66" y1="96" x2="66" y2="168" strokeWidth="3" strokeLinecap="round" />
          {/* Bails */}
          <line x1="47" y1="94" x2="69" y2="94" strokeWidth="3.5" strokeLinecap="round" />
          {/* Ground crease line */}
          <line x1="36" y1="168" x2="80" y2="168" strokeWidth="2" />

          {/* Iconic Dynamic IPL Batsman in mid-swing */}
          {/* Helmet */}
          <circle cx="112" cy="58" r="11" strokeWidth="2.5" fill="currentColor" fillOpacity="0.5" />
          <path d="M 116 62 L 126 62" strokeWidth="2.5" strokeLinecap="round" />
          {/* Torso & Shoulder rotation */}
          <path d="M 110 69 L 102 96 L 118 102 L 124 76 Z" strokeWidth="2.5" fill="currentColor" fillOpacity="0.3" />
          {/* Arms swinging bat */}
          <path d="M 118 78 L 138 68 L 152 48" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M 108 82 L 132 72" strokeWidth="3" strokeLinecap="round" />
          {/* Cricket Bat (high backlift / follow-through) */}
          <rect x="146" y="24" width="12" height="42" rx="3" transform="rotate(35 152 45)" strokeWidth="3" fill="currentColor" fillOpacity="0.7" />
          <line x1="148" y1="58" x2="140" y2="70" strokeWidth="3.5" strokeLinecap="round" />
          {/* Legs in dynamic front-foot stance */}
          <path d="M 104 98 L 94 130 L 84 158" strokeWidth="4" strokeLinecap="round" />
          <path d="M 116 102 L 128 132 L 140 162" strokeWidth="4" strokeLinecap="round" />
          {/* Batting Pads */}
          <rect x="78" y="132" width="16" height="26" rx="3" strokeWidth="2" fill="currentColor" fillOpacity="0.4" />
          <rect x="126" y="134" width="16" height="28" rx="3" strokeWidth="2" fill="currentColor" fillOpacity="0.4" />

          {/* High-Velocity Cricket Ball with Seam */}
          <circle cx="166" cy="116" r="13" strokeWidth="2.5" fill="#181818" />
          <path d="M 158 108 Q 166 116 174 124" strokeWidth="2" strokeDasharray="2 1" />
          {/* Motion trails */}
          <path d="M 182 120 C 188 122 192 126 194 132" strokeWidth="1.5" />
          <path d="M 180 110 C 188 110 194 114 196 120" strokeWidth="1.5" />
        </svg>
      );

    default:
      return null;
  }
};
